import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FieldsTable } from "./FieldsTable";
import { KV, secs, ts, usd } from "./primitives";

/** Dettaglio di un documento: run, segmenti, tabella dati con evidenze, vista lettore/FASE A normalizzate, revisioni. */
export const CaseDetailDialog = ({ cid, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!cid) return;
    setD(null); setErr(null);
    api.get(`/collaudo-lettore/cases/${cid}`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail, { status: e.response?.status })));
  }, [cid]);
  const c = d?.case;
  return (
    <Dialog open={!!cid} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[96vw] sm:max-w-6xl max-h-[92dvh] overflow-y-auto text-xs" data-testid="cl-case-dialog">
        <DialogHeader><DialogTitle className="text-sm">Documento {cid} — dettaglio del collaudo</DialogTitle></DialogHeader>
        {err && <div className="text-risk-800" data-testid="cl-case-error">{err}</div>}
        {!d && !err && <div className="text-muted-foreground">Lettura degli artefatti…</div>}
        {d && (
          <div className="space-y-3">
            <div className="grid gap-2 md:grid-cols-2 text-[11px]">
              <div>
                <KV k="Job / RFQ" v={<>{c.job_id} {c.job_id && <Link className="link-tech ml-1" to={`/reader-result/${c.job_id}`} data-testid="cl-case-open-result">→ in app</Link>}</>} />
                <KV k="Run (primo risultato congelato)" v={c.run_id} />
                <KV k="Esito run" v={c.outcome} />
                <KV k="Costo consuntivo / tetto" v={`${usd(c.real_cost_confirmed_usd)} / ${usd(c.authorized_cap_usd, 2)}`} />
                <KV k="Durata (upload → fine)" v={secs(c.duration_s)} />
                <KV k="Interventi Owner (recuperi)" v={c.owner_interventions} />
                <KV k="Congelato il" v={ts(c.frozen_at)} />
                <KV k="FASE A aperta il" v={ts(d.fase_a_opened_at)} />
              </div>
              <div>
                <KV k="PDF" v={`${c.pdf_kind || "—"} · ${c.pdf_readability || ""}`} />
                <KV k="SHA-256" v={String(c.sha256 || "").slice(0, 24) + "…"} />
                <KV k="Segmenti" v={(c.segments || []).map((s) => `${s.segment_id}:${s.state} $${s.real_cost_usd ?? "—"}`).join(" · ")} />
                <KV k="Blocco prudenziale" v={c.prudential_block ? "sì" : "no"} />
                {c.note && <div className="text-[10px] text-muted-foreground mt-1">{c.note}</div>}
              </div>
            </div>
            {(c.owner_decisions_required_by_app || []).length > 0 && (
              <div className="border border-warn-300 bg-warn-50 p-2 text-[11px]"><b>Decisioni Owner richieste dall'app:</b><ul className="list-disc pl-4">{c.owner_decisions_required_by_app.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
            )}
            <FieldsTable rows={d.rows} cases={[cid]} embedded defaultCase={cid} />
            {(d.revisions || []).map((r) => (
              <details key={r.revision_id} className="border border-ok-300 rounded-sm" data-testid={`cl-case-revision-${r.revision_id}`}>
                <summary className="px-2 py-1 cursor-pointer text-[11px] bg-ok-50 font-bold">Rilettura post-correzione {r.revision_id} — run {(r.run_id || "").slice(0, 18)}… · {r.run_status} · {usd(r.real_cost_confirmed_usd)}</summary>
                <div className="p-2"><FieldsTable rows={r.rows} cases={[cid]} embedded defaultCase={cid} readerLabel={`Lettore (rilettura ${r.revision_id}, run separato)`} /></div>
              </details>
            ))}
            <details className="border border-border rounded-sm">
              <summary className="px-2 py-1 cursor-pointer text-[11px] bg-muted font-bold">Vista normalizzata lettore / FASE A (JSON, sola lettura)</summary>
              <div className="grid gap-2 md:grid-cols-2 p-2">
                <pre className="text-[9px] bg-white border border-border p-2 overflow-auto max-h-80" data-testid="cl-case-reader-json">{JSON.stringify(d.reader_view, null, 1)}</pre>
                <pre className="text-[9px] bg-white border border-border p-2 overflow-auto max-h-80" data-testid="cl-case-fase-a-json">{JSON.stringify(d.fase_a_view, null, 1)}</pre>
              </div>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
